// Helper functions for authentication

/**
 * Parse and extract JWT token from URL
 */
export const parseToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  
  // Parse token from URL query params
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  
  if (token) {
    // Clean up URL after extracting token
    const newUrl = window.location.pathname;
    window.history.replaceState({}, document.title, newUrl);
    return token;
  }
  
  return null;
};

/**
 * Format error messages
 */
export const getErrorMessage = (error: any): string => {
  if (typeof error === 'string') return error;
  if (error?.response?.data?.error) return error.response.data.error;
  if (error?.message) return error.message;
  return 'An unknown error occurred';
};

/**
 * Get frontend URL (useful for callbacks)
 */
export const getFrontendUrl = (): string => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return process.env.NEXT_PUBLIC_FRONTEND_URL || 'http://localhost:3000';
};

export const DEFAULT_POST_AUTH_REDIRECT = '/scheduling';

const DISALLOWED_REDIRECT_PREFIXES = [
  '/login',
  '/auth',
  '/forgot-password',
  '/reset-password',
  '/onboarding',
];

const REDIRECT_VALIDATION_ORIGIN = 'https://scarlet-sync.invalid';

const isAllowedRedirectPath = (path: string): boolean => {
  if (!path.startsWith('/') || path.startsWith('//')) return false;

  // URL parsers treat backslashes and control characters in a path as separators
  // or discard them, which can turn an apparent local path into an external URL.
  const pathBeforeQueryOrHash = path.split(/[?#]/, 1)[0];
  if (/[\\\u0000-\u001F\u007F]/.test(pathBeforeQueryOrHash)) return false;

  try {
    const resolved = new URL(path, REDIRECT_VALIDATION_ORIGIN);
    // A dot segment can normalize an internal path to //host, which a later
    // browser navigation would interpret as a scheme-relative external URL.
    return resolved.origin === REDIRECT_VALIDATION_ORIGIN &&
      !resolved.pathname.startsWith('//') &&
      !DISALLOWED_REDIRECT_PREFIXES.some(
        (prefix) => resolved.pathname === prefix || resolved.pathname.startsWith(`${prefix}/`)
      );
  } catch {
    return false;
  }
};

export const getSafeRedirectPath = (
  redirect: string | null | undefined,
  fallback = DEFAULT_POST_AUTH_REDIRECT
): string => {
  const safeFallback = isAllowedRedirectPath(fallback) ? fallback : DEFAULT_POST_AUTH_REDIRECT;

  if (!redirect) return safeFallback;

  const trimmed = redirect.trim();
  if (!isAllowedRedirectPath(trimmed)) {
    return safeFallback;
  }

  return trimmed;
};

export const buildOnboardingDestination = (redirect: string | null | undefined): string => {
  const safeRedirect = getSafeRedirectPath(redirect);
  if (safeRedirect === DEFAULT_POST_AUTH_REDIRECT) {
    return '/onboarding';
  }

  return `/onboarding?redirect=${encodeURIComponent(safeRedirect)}`;
};

export const getPostAuthDestination = async (
  invokeOnboarding: () => Promise<{ data?: any; error?: any }>,
  requestedRedirect?: string | null,
  timeoutMs = 10000
): Promise<string> => {
  const safeRedirect = getSafeRedirectPath(requestedRedirect);

  try {
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Onboarding check timeout')), timeoutMs);
    });

    const { data, error } = await Promise.race([
      invokeOnboarding(),
      timeoutPromise,
    ]);

    if (error) {
      return buildOnboardingDestination(safeRedirect);
    }

    return data?.completed_at ? safeRedirect : buildOnboardingDestination(safeRedirect);
  } catch (err) {
    console.error('Post-auth onboarding check failed:', err);
    return buildOnboardingDestination(safeRedirect);
  }
};
